import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { gsap } from 'gsap/dist/gsap';
import Image from 'next/image';
import { FC, useRef, useState } from 'react';

import styles from './business-hub-how-ap-works.module.scss';

gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(useGSAP);

export interface BusinessHubHowApWorksStep {
    // name: string;
    description: string;
    descriptionMaxWidth?: string;
    icon: string;
    illustration: string;
}

export interface BusinessHubHowApWorksProps {
    title: string;
    steps: BusinessHubHowApWorksStep[];
}

const BusinessHubHowApWorks: FC<BusinessHubHowApWorksProps> = (props) => {
    const { title, steps = [] } = props;

    const containerRef = useRef<HTMLDivElement>(null);

    useGSAP(
        () => {
            const selector = gsap.utils.selector(containerRef);
            const sections = gsap.utils.toArray<HTMLDivElement>(selector(`.${styles.section}`));
            const progresses = gsap.utils.toArray<HTMLDivElement>(selector(`.${styles.progressItem}`));

            let tl = gsap.timeline({
                defaults: { gap: 1, duration: 1, ease: 'none' },
                scrollTrigger: {
                    trigger: containerRef.current,
                    pinnedContainer: containerRef.current,
                    pin: true,
                    start: 'bottom bottom',
                    scrub: true,
                    end: '+=300%',
                    invalidateOnRefresh: true,
                },
            });

            gsap.set(sections, { yPercent: 100, opacity: 0 });
            gsap.set(progresses, { yPercent: 100 });

            for (let index = 0; index < sections.length; index++) {
                const section = sections[index];
                const progress = progresses[index];

                if (index === 0) {
                    tl.to({}, {}, '+=1');
                    gsap.set([section], { yPercent: 0, y: 0, autoAlpha: 1 });
                    gsap.set(progress, { yPercent: 0, autoAlpha: 1, className: `${styles.progressItem} ${styles.active}` });
                    if (progresses[index + 1]) {
                        gsap.set(progresses[index + 1], { yPercent: 50, opacity: 1 });
                    }
                    continue;
                }

                const prevSection = sections[index - 1];
                const prevProgress = progresses[index - 1];
                const nextProgress = progresses[index + 1];

                tl.to(
                    {},
                    {
                        onStart: () => {
                            progress.classList.add(styles.active);
                            const subTL = gsap.timeline({}).to([progress, section], {
                                yPercent: 0,
                                opacity: 1,
                            });
                            if (prevSection) {
                                subTL.to(
                                    [prevSection, prevProgress],
                                    {
                                        yPercent: -50,
                                        opacity: 0,
                                    },
                                    '<'
                                );
                            }
                            if (nextProgress) {
                                subTL.to(
                                    nextProgress,
                                    {
                                        yPercent: 50,
                                        opacity: 1,
                                    },
                                    '<'
                                );
                            }
                        },
                        onReverseComplete: () => {
                            progress.classList.remove(styles.active);
                            const subTL = gsap
                                .timeline({})
                                .to([section], {
                                    yPercent: 100,
                                    opacity: 0,
                                })
                                .to(
                                    progress,
                                    {
                                        yPercent: 50,
                                        opacity: 1,
                                    },
                                    '<'
                                );

                            if (prevProgress) {
                                subTL
                                    .to(
                                        [prevSection],
                                        {
                                            yPercent: 0,
                                            opacity: 1,
                                        },
                                        '<'
                                    )
                                    .to(
                                        [prevProgress],
                                        {
                                            yPercent: 0,
                                            opacity: 1,
                                        },
                                        '<'
                                    );
                            }
                            if (nextProgress) {
                                subTL.to(
                                    nextProgress,
                                    {
                                        yPercent: 100,
                                        opacity: 0,
                                    },
                                    '<'
                                );
                            }
                        },
                    },
                    '+=1'
                );

                tl.to({}, {}, '+=0.5');
            }
        },
        { scope: containerRef }
    );

    if (!steps.length) return null;

    return (
        <div className={styles.container}>
            <div className={styles.background} />
            <div ref={containerRef} className={styles.box}>
                <div className={styles.parallaxContainer}>
                    <div className={styles.title}>{title}</div>
                    <div className={styles.scrollContent}>
                        <div className={styles.scrollContentOverlay}>
                            <div />
                            <div />
                            <div className={styles.progress}>
                                {steps.map((_, index) => (
                                    <div key={index} className={styles.progressItem}>
                                        <div className={styles.progressBox}>
                                            <span>{index + 1}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {steps.map((step, index) => {
                            return (
                                <section key={`step_${index}`} className={styles.section}>
                                    <div className={styles.sectionImage}>
                                        <Image
                                            src={step.illustration}
                                            alt=""
                                            style={{ objectFit: 'cover', objectPosition: 'center' }}
                                            fill={true}
                                            loading={'eager'}
                                            loader={({ src, width, quality }) => {
                                                return `${src}?width=${width}&quality=${quality ?? 75}`;
                                            }}
                                            className={styles.image}
                                        />
                                    </div>
                                    <div className={styles.sectionContent}>
                                        <div className={styles.sectionContentIcon}>
                                            <div className={styles.sectionContentIconBox}>
                                                <Image
                                                    src={step.icon}
                                                    alt=""
                                                    style={{ objectFit: 'contain', objectPosition: 'center' }}
                                                    fill
                                                    loader={({ src, width, quality }) => {
                                                        return `${src}?width=${width}&quality=${quality ?? 75}`;
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div
                                            className={styles.sectionContentDescription}
                                            style={{ '--des-max-width': step.descriptionMaxWidth ? `${step.descriptionMaxWidth}px` : null } as React.CSSProperties}
                                            dangerouslySetInnerHTML={{ __html: step.description }}
                                        />
                                    </div>
                                </section>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BusinessHubHowApWorks;
